import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const EMPTY_BRANCH_ROLE = {
  branchId: "",
  branchRole: "ACCOUNTANT",
};

const COMPANY_ROLE_OPTIONS = [
  { value: "COMPANY_ADMIN", labelKey: "userInvite.roles.companyAdmin" },
  { value: "MEMBER", labelKey: "userInvite.roles.branchUser" },
];

const BRANCH_ROLE_OPTIONS = [
  { value: "BRANCH_MANAGER", labelKey: "userInvite.roles.branchManager" },
  { value: "ACCOUNTANT", labelKey: "userInvite.roles.accountant" },
  { value: "VIEWER", labelKey: "userInvite.roles.viewer" },
];

const getBranchId = (branch) => branch?._id || branch?.id;

const InviteUserModal = ({ isOpen, onClose, onInvite, branches = [] }) => {
  const { t } = useTranslation("pages");
  const [email, setEmail] = useState("");
  const [companyRole, setCompanyRole] = useState("MEMBER");
  const [branchRoles, setBranchRoles] = useState([{ ...EMPTY_BRANCH_ROLE }]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const branchOptions = useMemo(() => {
    return branches
      .map((branch) => ({
        id: getBranchId(branch),
        name:
          branch?.name ||
          branch?.legalName ||
          branch?.branchName ||
          t("userInvite.branchFallback"),
        code: branch?.code || branch?.branchCode || "",
      }))
      .filter((branch) => branch.id);
  }, [branches, t]);

  if (!isOpen) return null;

  const resetForm = () => {
    setEmail("");
    setCompanyRole("MEMBER");
    setBranchRoles([{ ...EMPTY_BRANCH_ROLE }]);
    setErrors({});
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const validate = () => {
    const nextErrors = {};

    if (!email.trim()) {
      nextErrors.email = t("userInvite.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      nextErrors.email = t("userInvite.errors.emailInvalid");
    }

    if (!["COMPANY_ADMIN", "MEMBER"].includes(companyRole)) {
      nextErrors.companyRole = t("userInvite.errors.roleInvalid");
    }

    if (companyRole === "MEMBER") {
      if (branchOptions.length === 0) {
        nextErrors.branchRoles = t("userInvite.errors.createBranchFirst");
      } else if (!branchRoles.length) {
        nextErrors.branchRoles = t("userInvite.errors.branchAccessRequired");
      } else {
        const seenBranches = new Set();

        branchRoles.forEach((role, index) => {
          if (!role.branchId) {
            nextErrors[`branchId_${index}`] = t(
              "userInvite.errors.branchRequired",
            );
          }

          if (!role.branchRole) {
            nextErrors[`branchRole_${index}`] = t(
              "userInvite.errors.branchRoleRequired",
            );
          }

          if (role.branchId && seenBranches.has(role.branchId)) {
            nextErrors[`branchId_${index}`] = t(
              "userInvite.errors.branchAlreadyAssigned",
            );
          }

          if (role.branchId) {
            seenBranches.add(role.branchId);
          }
        });
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateBranchRole = (index, field, value) => {
    setBranchRoles((previous) =>
      previous.map((role, roleIndex) =>
        roleIndex === index ? { ...role, [field]: value } : role,
      ),
    );
  };

  const addBranchAccess = () => {
    setBranchRoles((previous) => [...previous, { ...EMPTY_BRANCH_ROLE }]);
  };

  const removeBranchAccess = (index) => {
    setBranchRoles((previous) =>
      previous.filter((_, roleIndex) => roleIndex !== index),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload =
      companyRole === "COMPANY_ADMIN"
        ? {
            email: email.trim().toLowerCase(),
            companyRole: "COMPANY_ADMIN",
            branchRoles: [],
          }
        : {
            email: email.trim().toLowerCase(),
            companyRole: "MEMBER",
            branchRoles: branchRoles.map((role) => ({
              branchId: role.branchId,
              branchRole: role.branchRole,
            })),
          };

    setIsSubmitting(true);

    try {
      await onInvite(payload);
      handleClose();
    } catch (error) {
      const status = error?.status || error?.response?.status;

      if (status === 403) {
        setErrors({
          submit: t("userInvite.errors.forbidden"),
        });
      } else if (status === 409) {
        setErrors({
          submit:
            error?.message || t("userInvite.errors.userExists"),
        });
      } else if (status === 404) {
        setErrors({
          submit: t("userInvite.errors.selectedBranchMissing"),
        });
      } else {
        setErrors({
          submit: error?.message || t("userInvite.errors.failed"),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompanyAdmin = companyRole === "COMPANY_ADMIN";
  const memberSubmitDisabled =
    companyRole === "MEMBER" && branchOptions.length === 0;

  return (
    <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {t("userInvite.title")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("userInvite.subtitle")}
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="w-9 h-9 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.submit && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {errors.submit}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("userInvite.fields.email")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder={t("userInvite.placeholders.email")}
            />
            {errors.email && (
              <p className="text-xs text-destructive mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("userInvite.fields.companyRole")}
            </label>
            <select
              value={companyRole}
              onChange={(event) => {
                const value = event.target.value;
                setCompanyRole(value);
                setErrors({});

                if (value === "COMPANY_ADMIN") {
                  setBranchRoles([]);
                }

                if (value === "MEMBER" && branchRoles.length === 0) {
                  setBranchRoles([{ ...EMPTY_BRANCH_ROLE }]);
                }
              }}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {COMPANY_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
            {errors.companyRole && (
              <p className="text-xs text-destructive mt-1">
                {errors.companyRole}
              </p>
            )}
          </div>

          {isCompanyAdmin ? (
            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
              {t("userInvite.adminNotice")}
            </div>
          ) : (
            <div className="border-t border-border pt-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    {t("userInvite.branchSection")}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("userInvite.branchHelp")}
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBranchAccess}
                  disabled={branchOptions.length === 0}
                >
                  {t("userInvite.actions.addBranchAccess")}
                </Button>
              </div>

              {branchOptions.length === 0 ? (
                <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-muted-foreground">
                  {t("userInvite.errors.createBranchFirst")}
                </div>
              ) : (
                <div className="space-y-3">
                  {branchRoles.map((role, index) => (
                    <div
                      key={`${role.branchId || "branch"}-${index}`}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start"
                    >
                      <div>
                        <select
                          value={role.branchId}
                          onChange={(event) =>
                            updateBranchRole(
                              index,
                              "branchId",
                              event.target.value,
                            )
                          }
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          <option value="">
                            {t("userInvite.placeholders.selectBranch")}
                          </option>
                          {branchOptions.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                              {branch.code ? ` (${branch.code})` : ""}
                            </option>
                          ))}
                        </select>
                        {errors[`branchId_${index}`] && (
                          <p className="text-xs text-destructive mt-1">
                            {errors[`branchId_${index}`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <select
                          value={role.branchRole}
                          onChange={(event) =>
                            updateBranchRole(
                              index,
                              "branchRole",
                              event.target.value,
                            )
                          }
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          {BRANCH_ROLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {t(option.labelKey)}
                            </option>
                          ))}
                        </select>
                        {errors[`branchRole_${index}`] && (
                          <p className="text-xs text-destructive mt-1">
                            {errors[`branchRole_${index}`]}
                          </p>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeBranchAccess(index)}
                        disabled={branchRoles.length === 1}
                      >
                        {t("userInvite.actions.remove")}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {errors.branchRoles && (
                <p className="text-xs text-destructive mt-2">
                  {errors.branchRoles}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || memberSubmitDisabled}
            >
              {isSubmitting
                ? t("userInvite.actions.sending")
                : t("userInvite.actions.send")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
